/**
 * GitHubDashboard - Admin dashboard for GitHub integration
 * Features: Update checker, content sync, changelog viewer, commit history
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Github, GitBranch, GitCommit, Tag, Download, RefreshCw, Settings,
  ExternalLink, FileText, Clock, User, AlertCircle, CheckCircle2,
  Loader2, ArrowUpCircle, FolderSync, Database, Star, GitFork,
  Eye, Calendar, Shield, Code, Webhook, Copy, Trash2, Plus, Play, FileCode, Key
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GitHubDashboard = () => {
  const [activeTab, setActiveTab] = useState('updates');
  const [showSettings, setShowSettings] = useState(false);
  const [syncDialog, setSyncDialog] = useState({ open: false, path: '', collection: '', eventId: '' });
  const queryClient = useQueryClient();
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Settings form state
  const [settings, setSettings] = useState({
    repo_owner: '',
    repo_name: '',
    access_token: '',
    content_repo_owner: '',
    content_repo_name: '',
    content_branch: 'main',
    auto_check_updates: true
  });

  // Fetch GitHub settings
  const { data: githubSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['github-settings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/settings`, { headers });
      if (!res.ok) return {};
      return res.json();
    },
    onSuccess: (data) => {
      if (data && Object.keys(data).length > 0) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    }
  });

  // Fetch app version
  const { data: appVersion } = useQuery({
    queryKey: ['app-version'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/app/version`);
      return res.json();
    }
  });

  // Fetch releases
  const { data: releasesData, isLoading: releasesLoading, refetch: refetchReleases } = useQuery({
    queryKey: ['github-releases'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/releases`, { headers });
      if (!res.ok) {
        if (res.status === 400) return null; // Not configured
        throw new Error('Failed to fetch releases');
      }
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner,
    retry: false
  });

  // Fetch commits
  const { data: commits, isLoading: commitsLoading, refetch: refetchCommits } = useQuery({
    queryKey: ['github-commits'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/commits`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch changelog
  const { data: changelog, isLoading: changelogLoading } = useQuery({
    queryKey: ['github-changelog'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/changelog`, { headers });
      if (!res.ok) return { content: '', filename: null };
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch repo info
  const { data: repoInfo, isLoading: repoLoading } = useQuery({
    queryKey: ['github-repo-info'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/repo-info`, { headers });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch webhook URL
  const { data: webhookInfo } = useQuery({
    queryKey: ['github-webhook-url'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/webhook/url`, { headers });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch webhook rules
  const { data: webhookRules, refetch: refetchRules } = useQuery({
    queryKey: ['github-webhook-rules'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/webhook/rules`, { headers });
      if (!res.ok) return { sync_rules: [], webhook_secret: '' };
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch webhook logs
  const { data: webhookLogs, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['github-webhook-logs'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/webhook/logs?limit=20`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch Actions templates
  const { data: actionsTemplates } = useQuery({
    queryKey: ['github-actions-templates'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/actions/templates`, { headers });
      if (!res.ok) return { templates: [] };
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Fetch required secrets
  const { data: requiredSecrets } = useQuery({
    queryKey: ['github-actions-secrets'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/github/actions/secrets-needed`, { headers });
      if (!res.ok) return { secrets: [] };
      return res.json();
    },
    enabled: !!githubSettings?.repo_owner
  });

  // Selected template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Fetch template details
  const fetchTemplate = async (templateId) => {
    const res = await fetch(`${API_URL}/api/github/actions/templates/${templateId}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setSelectedTemplate(data);
    }
  };

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/github/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to save settings');
      return res.json();
    },
    onSuccess: () => {
      toast.success('GitHub settings saved');
      queryClient.invalidateQueries(['github-settings']);
      queryClient.invalidateQueries(['github-releases']);
      queryClient.invalidateQueries(['github-repo-info']);
      setShowSettings(false);
    },
    onError: (err) => {
      toast.error(err.message);
    }
  });

  // Sync content mutation
  const syncContentMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/github/sync-content`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Sync failed');
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setSyncDialog({ open: false, path: '', collection: '', eventId: '' });
    },
    onError: (err) => {
      toast.error(err.message);
    }
  });

  // Save webhook rules mutation
  const saveRulesMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/github/webhook/rules`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to save rules');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Webhook rules saved');
      refetchRules();
    },
    onError: (err) => {
      toast.error(err.message);
    }
  });

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isConfigured = githubSettings?.repo_owner && githubSettings?.repo_name;

  return (
    <div className="space-y-6" data-testid="github-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-900 rounded-lg">
            <Github className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">GitHub Integration</h2>
            <p className="text-slate-500 text-sm">
              Updates, content sync, and release management
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-1" />
            Settings
          </Button>
          {isConfigured && (
            <Button variant="outline" size="sm" onClick={() => {
              refetchReleases();
              refetchCommits();
            }}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Not Configured Alert */}
      {!isConfigured && !settingsLoading && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>GitHub Not Configured</AlertTitle>
          <AlertDescription>
            Configure your GitHub repository to enable updates, content sync, and release tracking.
            <Button variant="link" className="p-0 h-auto ml-2" onClick={() => setShowSettings(true)}>
              Configure Now →
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* App Version & Update Banner */}
      {isConfigured && releasesData?.update_available && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <ArrowUpCircle className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800">Update Available!</AlertTitle>
          <AlertDescription className="text-emerald-700">
            A new version of EventFlow is available. Check the Updates tab to see what's new.
          </AlertDescription>
        </Alert>
      )}

      {/* Current Version Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Code className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Version</p>
                <p className="text-2xl font-bold">{appVersion?.version || '1.0.0'}</p>
              </div>
            </div>
            {repoInfo && (
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4" />
                  <span>{repoInfo.stars}</span>
                </div>
                <div className="flex items-center gap-1">
                  <GitFork className="h-4 w-4" />
                  <span>{repoInfo.forks}</span>
                </div>
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>{repoInfo.open_issues} issues</span>
                </div>
                <a 
                  href={repoInfo.html_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  View on GitHub
                </a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      {isConfigured && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
            <TabsTrigger value="updates" className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4" />
              Updates
              {releasesData?.update_available && (
                <Badge className="bg-emerald-500 text-white ml-1">New</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="commits" className="flex items-center gap-2">
              <GitCommit className="h-4 w-4" />
              Commits
            </TabsTrigger>
            <TabsTrigger value="changelog" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Changelog
            </TabsTrigger>
            <TabsTrigger value="sync" className="flex items-center gap-2">
              <FolderSync className="h-4 w-4" />
              Content Sync
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex items-center gap-2">
              <Webhook className="h-4 w-4" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="actions" className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Actions
            </TabsTrigger>
          </TabsList>

          {/* Updates Tab */}
          <TabsContent value="updates" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Available Releases
                </CardTitle>
                <CardDescription>
                  View and track releases from the GitHub repository
                </CardDescription>
              </CardHeader>
              <CardContent>
                {releasesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : releasesData?.releases?.length > 0 ? (
                  <div className="space-y-4">
                    {releasesData.releases.map((release, index) => (
                      <div 
                        key={release.id}
                        className={`p-4 rounded-lg border ${
                          release.is_newer 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : index === 0 
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <Tag className={`h-5 w-5 ${release.is_newer ? 'text-emerald-600' : 'text-slate-600'}`} />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-lg">{release.tag_name}</span>
                                {release.is_newer && (
                                  <Badge className="bg-emerald-500">New</Badge>
                                )}
                                {release.prerelease && (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                                    Pre-release
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-600">{release.name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">
                              {formatDate(release.published_at)}
                            </span>
                            <a 
                              href={release.html_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="outline" size="sm">
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </a>
                          </div>
                        </div>
                        {release.body && (
                          <div className="mt-3 text-sm text-slate-600 prose prose-sm max-w-none">
                            <ReactMarkdown>{release.body.slice(0, 500)}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No releases found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commits Tab */}
          <TabsContent value="commits" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitCommit className="h-5 w-5" />
                  Recent Commits
                </CardTitle>
                <CardDescription>
                  Latest changes from the repository
                </CardDescription>
              </CardHeader>
              <CardContent>
                {commitsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : commits?.length > 0 ? (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {commits.map((commit, index) => (
                        <div 
                          key={commit.full_sha}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-mono">
                              {commit.sha}
                            </div>
                            {index < commits.length - 1 && (
                              <div className="w-0.5 h-full bg-slate-200 mt-2" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">
                              {commit.message}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {commit.author}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(commit.date)}
                              </span>
                            </div>
                          </div>
                          <a 
                            href={commit.html_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <GitCommit className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No commits found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Changelog Tab */}
          <TabsContent value="changelog" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Changelog
                  {changelog?.filename && (
                    <Badge variant="outline">{changelog.filename}</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Release notes and version history
                </CardDescription>
              </CardHeader>
              <CardContent>
                {changelogLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : changelog?.content ? (
                  <ScrollArea className="h-[500px]">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{changelog.content}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No changelog found in repository</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Sync Tab */}
          <TabsContent value="sync" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderSync className="h-5 w-5" />
                  Content Sync
                </CardTitle>
                <CardDescription>
                  Sync event content (speakers, schedules, etc.) from GitHub
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {/* Preset sync options */}
                  {[
                    { label: 'Speakers', path: 'content/speakers.json', collection: 'speakers', icon: User },
                    { label: 'Sessions', path: 'content/sessions.json', collection: 'sessions', icon: Calendar },
                    { label: 'Sponsors', path: 'content/sponsors.json', collection: 'sponsors', icon: Star },
                  ].map((item) => (
                    <div 
                      key={item.collection}
                      className="flex items-center justify-between p-4 rounded-lg border bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg border">
                          <item.icon className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-sm text-slate-500">{item.path}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => setSyncDialog({ 
                          open: true, 
                          path: item.path, 
                          collection: item.collection,
                          eventId: ''
                        })}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Sync
                      </Button>
                    </div>
                  ))}

                  <Separator className="my-4" />

                  {/* Custom sync */}
                  <div className="p-4 rounded-lg border border-dashed">
                    <p className="font-medium mb-2">Custom Content Sync</p>
                    <p className="text-sm text-slate-500 mb-4">
                      Sync any JSON file from your GitHub repository to a MongoDB collection
                    </p>
                    <Button 
                      variant="outline"
                      onClick={() => setSyncDialog({ open: true, path: '', collection: '', eventId: '' })}
                    >
                      <FolderSync className="h-4 w-4 mr-1" />
                      Custom Sync
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="mt-4 space-y-4">
            {/* Webhook URL Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Webhook className="h-5 w-5" />
                  Webhook Configuration
                </CardTitle>
                <CardDescription>
                  Configure GitHub to send push events for auto-sync
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Webhook URL */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium">Webhook URL</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Add this URL in GitHub → Settings → Webhooks → Add webhook
                  </p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={webhookInfo?.webhook_url || ''} 
                      readOnly 
                      className="font-mono text-sm bg-white"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(webhookInfo?.webhook_url || '')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Webhook Secret */}
                <div className="space-y-2">
                  <Label>Webhook Secret (optional)</Label>
                  <p className="text-xs text-slate-500">
                    Add a secret to verify webhook requests are from GitHub
                  </p>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="password"
                      placeholder="Enter a secret or leave empty"
                      defaultValue={webhookRules?.webhook_secret || ''}
                      id="webhook-secret-input"
                    />
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById('webhook-secret-input');
                        saveRulesMutation.mutate({
                          sync_rules: webhookRules?.sync_rules || [],
                          webhook_secret: input.value
                        });
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>

                {/* Setup Instructions */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800 mb-2">Setup Instructions</p>
                  <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                    <li>Go to your GitHub repository → Settings → Webhooks</li>
                    <li>Click "Add webhook"</li>
                    <li>Paste the Webhook URL above</li>
                    <li>Set Content type to <code className="bg-blue-100 px-1 rounded">application/json</code></li>
                    <li>Select "Just the push event"</li>
                    <li>Click "Add webhook"</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Sync Rules Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderSync className="h-5 w-5" />
                  Auto-Sync Rules
                </CardTitle>
                <CardDescription>
                  Define which files trigger automatic syncs when pushed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(webhookRules?.sync_rules || []).map((rule, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg border bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <Switch 
                          checked={rule.enabled !== false}
                          onCheckedChange={(checked) => {
                            const newRules = [...(webhookRules?.sync_rules || [])];
                            newRules[index] = { ...rule, enabled: checked };
                            saveRulesMutation.mutate({ sync_rules: newRules });
                          }}
                        />
                        <div>
                          <p className="font-mono text-sm">{rule.path_pattern}</p>
                          <p className="text-xs text-slate-500">→ {rule.target_collection}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          const newRules = (webhookRules?.sync_rules || []).filter((_, i) => i !== index);
                          saveRulesMutation.mutate({ sync_rules: newRules });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {(!webhookRules?.sync_rules || webhookRules.sync_rules.length === 0) && (
                    <div className="text-center py-4 text-slate-400">
                      <p>No sync rules configured. Default rules will be used.</p>
                    </div>
                  )}

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      const newRules = [...(webhookRules?.sync_rules || []), {
                        path_pattern: 'content/new.json',
                        target_collection: 'custom',
                        enabled: true
                      }];
                      saveRulesMutation.mutate({ sync_rules: newRules });
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Rule
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Webhook Logs Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Webhook Logs
                    </CardTitle>
                    <CardDescription>
                      Recent webhook events received
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => refetchLogs()}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : webhookLogs?.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {webhookLogs.map((log) => (
                        <div 
                          key={log.id}
                          className={`p-3 rounded-lg border ${
                            log.processed ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={log.processed ? 'default' : 'secondary'}>
                                {log.event_type}
                              </Badge>
                              <span className="text-sm text-slate-600">{log.repository}</span>
                            </div>
                            <span className="text-xs text-slate-400">
                              {formatDate(log.received_at)}
                            </span>
                          </div>
                          {log.branch && (
                            <p className="text-xs text-slate-500 mt-1">
                              Branch: {log.branch} • {log.modified_files?.length || 0} files changed
                            </p>
                          )}
                          {log.sync_results?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {log.sync_results.map((result, i) => (
                                <div 
                                  key={i}
                                  className={`text-xs px-2 py-1 rounded ${
                                    result.status === 'success' 
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : result.status === 'error'
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {result.file} → {result.collection}: {result.status}
                                  {result.items_synced && ` (${result.items_synced} items)`}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Webhook className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No webhook events received yet</p>
                    <p className="text-sm mt-1">Configure the webhook in GitHub to start receiving events</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="mt-4 space-y-4">
            {/* Workflow Templates Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode className="h-5 w-5" />
                  Workflow Templates
                </CardTitle>
                <CardDescription>
                  Ready-to-use GitHub Actions workflows for CI/CD and automation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {actionsTemplates?.templates?.map((template) => (
                    <div 
                      key={template.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedTemplate?.id === template.id 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => fetchTemplate(template.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg border">
                            <Play className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">{template.name}</p>
                            <p className="text-sm text-slate-500">{template.description}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs">
                          {template.filename}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Selected Template Preview */}
            {selectedTemplate && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Code className="h-5 w-5" />
                        {selectedTemplate.name}
                      </CardTitle>
                      <CardDescription>
                        {selectedTemplate.filename}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(selectedTemplate.content)}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const blob = new Blob([selectedTemplate.content], { type: 'text/yaml' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = selectedTemplate.filename;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success('Downloaded ' + selectedTemplate.filename);
                        }}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Setup Instructions */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="font-medium text-amber-800 mb-2">Setup Instructions</p>
                    <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                      {selectedTemplate.setup_instructions?.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>

                  {/* Code Preview */}
                  <div className="relative">
                    <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-sm font-mono">
                      <code>{selectedTemplate.content}</code>
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Required Secrets Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Required Secrets
                </CardTitle>
                <CardDescription>
                  Add these secrets in GitHub → Settings → Secrets → Actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {requiredSecrets?.secrets?.map((secret) => (
                    <div 
                      key={secret.name}
                      className="p-3 rounded-lg border bg-slate-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-mono font-medium text-sm">{secret.name}</p>
                          <p className="text-xs text-slate-500">{secret.description}</p>
                          {secret.required_for?.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {secret.required_for.map((wf) => (
                                <Badge key={wf} variant="outline" className="text-xs">
                                  {wf}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {secret.value && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyToClipboard(secret.value)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Github className="h-5 w-5" />
              GitHub Settings
            </DialogTitle>
            <DialogDescription>
              Configure your GitHub repository for updates and content sync
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Repository Owner</Label>
                <Input 
                  placeholder="e.g., octocat"
                  value={settings.repo_owner}
                  onChange={(e) => setSettings({ ...settings, repo_owner: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Repository Name</Label>
                <Input 
                  placeholder="e.g., eventflow"
                  value={settings.repo_name}
                  onChange={(e) => setSettings({ ...settings, repo_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Access Token (optional, for private repos)</Label>
              <Input 
                type="password"
                placeholder="ghp_xxxxxxxxxxxx"
                value={settings.access_token}
                onChange={(e) => setSettings({ ...settings, access_token: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                Generate a token at GitHub → Settings → Developer settings → Personal access tokens
              </p>
            </div>

            <Separator />

            <p className="text-sm font-medium">Content Repository (optional)</p>
            <p className="text-xs text-slate-500 mb-2">
              Use a separate repository for content sync (speakers, schedules, etc.)
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Repo Owner</Label>
                <Input 
                  placeholder="Same as above if empty"
                  value={settings.content_repo_owner}
                  onChange={(e) => setSettings({ ...settings, content_repo_owner: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Content Repo Name</Label>
                <Input 
                  placeholder="Same as above if empty"
                  value={settings.content_repo_name}
                  onChange={(e) => setSettings({ ...settings, content_repo_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content Branch</Label>
              <Input 
                placeholder="main"
                value={settings.content_branch}
                onChange={(e) => setSettings({ ...settings, content_branch: e.target.value })}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-check for Updates</Label>
                <p className="text-xs text-slate-500">Check for new releases periodically</p>
              </div>
              <Switch 
                checked={settings.auto_check_updates}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_check_updates: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => saveSettingsMutation.mutate(settings)}
              disabled={saveSettingsMutation.isLoading}
            >
              {saveSettingsMutation.isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Dialog */}
      <Dialog open={syncDialog.open} onOpenChange={(open) => setSyncDialog({ ...syncDialog, open })}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderSync className="h-5 w-5" />
              Sync Content from GitHub
            </DialogTitle>
            <DialogDescription>
              Import JSON content from your GitHub repository
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>File Path in Repository</Label>
              <Input 
                placeholder="e.g., content/speakers.json"
                value={syncDialog.path}
                onChange={(e) => setSyncDialog({ ...syncDialog, path: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Collection</Label>
              <Select 
                value={syncDialog.collection}
                onValueChange={(v) => setSyncDialog({ ...syncDialog, collection: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select collection" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="speakers">Speakers</SelectItem>
                  <SelectItem value="sessions">Sessions</SelectItem>
                  <SelectItem value="sponsors">Sponsors</SelectItem>
                  <SelectItem value="delegates">Delegates</SelectItem>
                  <SelectItem value="tiers">Ticket Tiers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Event ID (optional)</Label>
              <Input 
                placeholder="Leave empty for global content"
                value={syncDialog.eventId}
                onChange={(e) => setSyncDialog({ ...syncDialog, eventId: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                If provided, content will be associated with this event
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncDialog({ open: false, path: '', collection: '', eventId: '' })}>
              Cancel
            </Button>
            <Button 
              onClick={() => syncContentMutation.mutate({
                path: syncDialog.path,
                target_collection: syncDialog.collection,
                event_id: syncDialog.eventId || null
              })}
              disabled={syncContentMutation.isLoading || !syncDialog.path || !syncDialog.collection}
            >
              {syncContentMutation.isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <Download className="h-4 w-4 mr-1" />
              Sync Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GitHubDashboard;
